import {
  Division,
  Role,
  Community,
  User,
  State,
  Permission,
  Country,
  TeamMemberType as ContactType,
  Company,
  Phase,
  PhaseTip,
  Plan,
  Elevation,
  Lot,
  Event,
  TeamMember as Contact,
  Document,
  AttendeesUnion,
  LotDocument,
  DefaultPhaseTip
} from '@tripointe/tripointe-cms/data-access';
import { Conversation, DropdownGroup, DropdownItem } from '@tripointe/tripointe-cms/types';

export const createOption = (text: string, value: number | string) => ({
  text,
  value
});

export const divisionToOption = (division: Partial<Division> | any) =>
  createOption(division.name, division.id);

export const divisionWithPhasesToOptionsGroup = (valueAsId: boolean) => (
  division: Division
): DropdownGroup => ({
  label: division.name,
  options: division.phases.map(phase =>
    valueAsId ? phaseToOption(phase) : phaseNameToOption(phase.name)
  )
});

export const communityToOption = (community: Community) =>
  createOption(community.name, community.id);

export const lotToOption = (lot: Lot) => createOption(lot.address, lot.id);

export const communityToAutocompleteSearchOption = (
  community: Partial<Community>,
  field: string
) => {
  return createOption(community[field], community[field]);
};

export const divisionToAutocompleteSearchOption = (division: Partial<Division>, field: string) => {
  return createOption(division[field], division[field]);
};

export const eventToAutocompleteSearchOption = (event: Partial<Event>, field: string) => {
  return createOption(event[field], event[field]);
};

export const roleToOption = (role: Role) => createOption(role.label, role.id);

export const hoursToOption = (hours: number) => {
  const label = hours === 5 ? `${hours}+` : `${hours}`;

  return createOption(label, hours);
};

export const userToAutocompleteSearchOption = (user: Partial<User>, field: string) => {
  return createOption(user[field], user[field]);
};
// need to add typing from backend
export const homeScreenToOption = (permission: Permission) =>
  createOption(permission.label, permission.id);

export const userToOption = (user: Partial<User>) => {
  return createOption(user.fullName, user.id);
};

export const eventToOption = (event: Partial<Event>) => {
  return createOption(event.name, event.id);
};

const symbolSearchStart = 2;
export const isValidSearchQuery = (query: string, customMinLength?: number): boolean => {
  const trimmedValue = (query || '').trim();

  return trimmedValue && trimmedValue.length >= (customMinLength || symbolSearchStart);
};

export const stateToOption = (state: Partial<State>) => {
  return createOption(state.label, state.id);
};

export const countryToOption = (country: Partial<Country>) => {
  return createOption(country.label, country.id);
};

export const contactTypeToOption = (contactType: Partial<ContactType>) => {
  return createOption(contactType.label, contactType.id);
};

export const companyToOption = (company: Partial<Company>) => {
  return createOption(company.name, company.id);
};

export const companyToOptionForFilter = (company: Partial<Company>) => {
  return createOption(company.name, company.name);
};

export const phaseToOption = (phase: Partial<Phase>) => {
  return createOption(phase.name, phase.id);
};

export const planToOption = (plan: Partial<Plan>) => {
  return createOption(plan.name, plan.id);
};

export const elevationToOption = (elevation: Partial<Elevation>) => {
  return createOption(elevation.name, elevation.id);
};

export const homeToAutocompleteSearchOption = (home: any, field: string) => {
  return createOption(home[field], home[field]);
};

export const homePhaseToOption = (phaseName: string) => {
  return createOption(phaseName, phaseName);
};

export const phaseNameToOption = (phaseName: string) => {
  return createOption(phaseName, phaseName);
};

export const phaseNameToOption2 = (phase: Partial<Phase>) => {
  return createOption(phase.name, phase.name);
};

export const optionMatchValue = (valueToMatch: number) => (option: DropdownItem) =>
  valueToMatch === option.value;

export const contactToAutocompleteSearchOption = (contact: Partial<Contact>, field: string) => {
  return createOption(contact[field], contact[field]);
};

export const contactTypeToAutocompleteSearchOption = (
  contactType: Partial<ContactType>,
  field: string
) => {
  return createOption(contactType[field], contactType[field]);
};

export const documentToAutocompleteSearchOption = (document: Partial<Document>, field: string) => {
  return createOption(document[field], document[field]);
};

export const phaseTipToAutocompleteSearchOption = (
  phaseTip: Partial<PhaseTip> | Partial<DefaultPhaseTip>,
  field: string
) => {
  return createOption(phaseTip[field], phaseTip[field]);
};

export const planToAutocompleteSearchOption = (plan: Partial<Plan>, field: string) => {
  return createOption(plan[field], plan[field]);
};

export const buyerNameToSearchOption = (firstName: string, lastName: string) => {
  const fullName = `${firstName} ${lastName}`;

  return createOption(fullName, fullName);
};

export const attendeeNameToOption = (fullName: string) => {
  return createOption(fullName, fullName);
};

export const teamMemberTypeToOption = (teamMemberTypeName: string) => {
  return createOption(teamMemberTypeName, teamMemberTypeName);
};

export const buyerNameToOptionForFilter = (fullName: string) => {
  return createOption(fullName, fullName);
};

export const lotDocumentToAutocompleteSearchOption = (
  lotDocument: Partial<LotDocument | Document>,
  field: string
) => {
  return createOption(lotDocument[field], lotDocument[field]);
};

export const homeAddressToOptionForFilter = (address: string) => {
  return createOption(address, address);
};

export const contactNameToOptionForFilter = (groupName: string) => {
  return createOption(groupName, groupName);
};
